import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import memoize from 'memoize-one';
import isEqual from 'lodash/isEqual';
import { styled } from '@compiled/react';

import { EmojiResource } from '@atlaskit/emoji/resource';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import UFOLabel from '@atlaskit/react-ufo/label';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { EditorForgeExtensionConfigHelperRenderer } from '@atlassian/xen-editor-provider';

import { useCommentsPanelActions } from '@confluence/comments-panel-utils';
import { EditorCommentShortcutListener } from '@confluence/comments-shortcuts';
import { InlineCommentsProvider, ObjectCommentProvider } from '@confluence/comment-context';
import { fg } from '@confluence/feature-gating';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { PageCommentsPubSubHandler } from '@confluence/comments-pubsub/entry-points/pageComments';
import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import { expValNoExposure } from '@confluence/feature-experiments';
import { useObjectSidebarActions, useIsObjectSidebarShown } from '@confluence/object-sidebar-api';
import type { LoadingPriority } from '@confluence/loadable';
import {
	ObjectSidebarControl,
	EditorObjectSidebarPanel,
} from '@confluence/object-sidebar-components';
import { useIsDesktop } from '@confluence/desktop-native-bridge';

// The DO_NOT_USE here is an artifact of an old implementation. We can, in fact, use this

import {
	SmartButtonConfigPanel as EditorContextSmartButtonConfigPanel,
	useSmartButtonConfigPanel,
	useSmartButtonPremiumGate,
} from '@confluence/automation-extensions';
import { QuickReload, PageMode } from '@confluence/quick-reload';
import { keys, confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager';
import { waitForGlobal } from '@confluence/wrm';
import {
	VIEW_ALL_MACRO_EXPERIENCE,
	ExperienceTimeout,
	ExperienceTrackerContext,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	ExperienceStart,
	OBJECT_HEADER_EXPERIENCE,
} from '@confluence/experience-tracker';
import { useRetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent';
import { EditorAnalyticsListener } from '@confluence/fabric-analytics-listeners';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { useSessionData } from '@confluence/session-data';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare';
import type { WithFlagsProps } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import type { WithDialogsProps } from '@confluence/dialogs';
import { withDialogs } from '@confluence/dialogs';
import {
	TriggerSaveIndicatorContext,
	SaveIndicatorContextProvider,
} from '@confluence/save-indicator';
// Metrics and Instrumentation related
import { PageLoadMark } from '@confluence/browser-metrics';
import { ObjectCommentWrapper } from '@confluence/object-comments';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
// Editor common functionalities and providers
import {
	useGetChannelVariables,
	type WithDocChangeContextProps,
	type ProseMirrorRenderedDataType,
} from '@confluence/editor-features';
import {
	// HOCs
	withEditorActions,
	// Hooks
	useActivityProvider,
	useAutoformattingProvider,
	useContextIdentifierProvider,
	useDisabledAnalytics,
	useDraftSyncAndPublish,
	useEditorAnalyticsEvent,
	useEditorFeatureFlags,
	useEditorTitleFocus,
	getFindReplaceOptions,
	useHandleCollabError,
	useInjectDraftContent,
	useMediaOptions,
	useMentionProvider,
	useSendContentAgeAnalytics,
	useSearchProvider,
	useSpinner,
	useEditorPluginAIOptions,
	useEditorPluginAIFeedbackHandlers,
	useEditorPluginAICollabConnector,
	useDocumentActive,
	useEditorInlineCommentsPubSub,
	useEditorDraftSyncAction,
	// Components
	EditorWrapper,
	Spinner,
	EditorDisplayWrapper,
	EditorLinkingFeatures,
	PageLoadWrapper,
	// Intercom.
	EditorPluginAIWithFeedback,
	withDocChangeContext,
	useEditorAIOptIn,
	useScrollOnUrlChange,
	withSmartCardProviderContext,
	useEditorAIReplacingTitle,
	useSmartElementRecommendations,
} from '@confluence/editor-features';
import { EditorContextContentCreationPanelWrapper } from '@confluence/editor-templates';
import {
	EditorContextPanelContextProvider,
	EditorContextPanelView,
} from '@confluence/editor-context-panel';
import { useEditorAnnotationProviderOptions } from '@confluence/annotation-provider';
import { EditorAnnotationProviderComponents } from '@confluence/annotation-provider-inline-comments/entry-points/editor';
import {
	useInlineCommentsActions,
	useEditorAnnotationsActions,
	useCommentRefetch,
} from '@confluence/inline-comments-hooks';
import { useCommentsDataActions, useUpdateAnnotations } from '@confluence/comments-data';
import { useUnreadCommentsActions, useUnreadCommentsState } from '@confluence/unread-comments';
import {
	useExtensionOptions,
	useEditorSSRExtensionHandlers,
} from '@confluence/fabric-extension-handlers';
import { getEmojiProvider, getTaskDecisionProvider } from '@confluence/fabric-providers';
import { useProfilecardProvider } from '@confluence/fabric-providers/entry-points/useProfileCardProvider';
import { MentionsNotificationsWrapper } from '@confluence/mentions-notifications';
// Editor UI components
import { DocumentEditPageTitle } from '@confluence/document-title';
import { EditorTypes, PreviewPageComponent } from '@confluence/editor-preview';
import {
	ContentAppearanceContextProvider,
	ContentAppearanceContextConsumer,
	getContentAppearanceForAtlaskit,
	type FullPageAppearances,
} from '@confluence/content-appearance';
import { PageContentRenderer } from '@confluence/content-renderer';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context';
import {
	EditorRevertDialog,
	useEditorRevertDialogContext,
	withRevertContext,
} from '@confluence/editor-revert-dialog';
import { OnboardingEditorModalPrompt } from '@confluence/onboarding-editor';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import type {
	EditorWrapperProps,
	ContentTypeType,
} from '@confluence/editor-features/entry-points/types';
import {
	EditorConnectionBanner,
	EditorConnectionBannerOfflineMode,
} from '@confluence/browser-status-indicator';
import { EditorDistinctionBanner } from '@confluence/live-pages-features/entry-points/EditorDistinctionBanner';
import { EDITOR, MacroExperienceStart } from '@confluence/macro-tracker';
import {
	useRouteActions,
	createSingleQueryParamHook,
} from '@confluence/route-manager/entry-points/RouteState';
import { withEditorModalContext } from '@confluence/editor-modal';
import { scrollToLastViewedPosition } from '@confluence/editor-scroll-position/entry-points/scrollToLastViewedPosition';
import { measureTimeToEdit } from '@confluence/editor-scroll-position/entry-points/measureTimeToEdit';
import {
	CollabServiceProperty,
	useConnecting,
	useNativeCollabConnected,
	useNativeCollabState,
	type NativeCollabInjectedProps,
} from '@confluence/native-collab';
import {
	EditorContextConnectionsPanel,
	useConnectionsPanel,
} from '@confluence/app-referentiality/entry-points/EditorContextConnectionsPanel';
import { getReferentialityContext } from '@confluence/app-referentiality';
import { getMarketplaceAppsContainer } from '@confluence/app-suggestions/entry-points';
import { useEditSource } from '@confluence/load-edit-page/entry-points/useEditSource';
import { ConditionalPublish } from '@confluence/conditional-publish-dialog';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import {
	LivePageReleasePhase,
	useIsLivePagesFeatureEnabled,
} from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { ConvertToLivePageFlag } from '@confluence/live-pages-features/entry-points/ConvertToLivePageFlag';
import { useContentTypesTitleUpdate } from '@confluence/content-types-header/entry-points/useContentTypesTitleUpdate';
import { EditorObjectHeaderContextProvider } from '@confluence/content-types-header/entry-points/EditorObjectHeaderContextProvider';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange';
import {
	EditorContextCustomSitesConfigPanelEntryPoint,
	useCustomExtensionConfigPanel,
} from '@confluence/custom-sites-extensions/entry-points/editorConfigPanel';
import {
	AutoConvertEvent,
	AutoConvertActionSubject,
	sendEditorConversionAnalyticsPageEvent,
	useAutoConvertState,
} from '@confluence/editor-conversion';
import { useSchedulePublishFlagsWatcher } from '@confluence/editor-schedule-publish';
import {
	EditorPrimaryToolbarComponents,
	PrimaryToolbarContextProvider,
	usePrimaryToolbarContext,
} from '@confluence/editor-primary-toolbar-components';
import { ContentComponentsContextProvider } from '@confluence/title-area-content-components';
import { EditorActionBar, LivePagesFeedbackBannerLoader } from '@confluence/editor-action-bar';
import { ContentTypesHeader } from '@confluence/content-types-header';
import { useSpaceId, useSpaceHomepageId } from '@confluence/space-utils';
import {
	getBlankPageQuickActionsExperimentEnabled,
	getBPQAMilestone3ExperimentEnabled,
	useShouldShowBlankPageQuickActions,
	BlankPageQuickActions,
	BlankPageQuickActionsTemplatePreview,
	BlankPageQuickActionsProvider,
} from '@confluence/blank-page-quick-actions';
import { useContextualInviteNudge } from '@confluence/onboarding-helpers/entry-points/hooks/useContextualInviteNudge';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries';
import { InlineCommentMode } from '@confluence/inline-comments-common/entry-points/enum';
import { useInlineCommentQueryParams } from '@confluence/comment';
import { getSortedMarkerRefs } from '@confluence/comments-util';
import { useInviteFromLinkExperimentEditor } from '@confluence/experiment-invite-on-link-insert';
import {
	HeadingLinksStyleWrapper,
	useHeadingLinksPathBuilder,
} from '@confluence/editor-heading-links';
import { EditorDatabasesOnboarding } from '@confluence/databases-onboarding-ga';
import { GuestMentionsNotificationsWrapper } from '@confluence/external-collab-ui/entry-points/GuestMentionsNotificationsWrapper';
import { CompanyHubBannerLoader } from '@confluence/company-hub/entry-points/CompanyHubBanner';
import { HubEditorWrapper } from '@confluence/company-hub/entry-points/hub-layout';
import { MultiMacroQueryContainer } from '@confluence/fabric-extension-queries';
import { useRequestToEditContent } from '@confluence/no-permission/entry-points/useRequestToEditContent';
import { ConfluenceRecapModalLoadable } from '@confluence/confluence-recap/entry-points/ConfluenceRecapModal';
import { useExtensionRendererViewOptions } from '@confluence/fabric-extension-lib/entry-points/useExtensionRendererViewOptions';
import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { PageFooterComments } from '@confluence/page-footer-comments';
import {
	usePublishModalMountActions,
	usePrePublishTitleValidation,
	PublishModalMountPoint,
	PublishModalKeyboardShortcuts,
} from '@confluence/publish-modal';
import type { LegacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import { ClassificationModalContextProvider } from '@confluence/data-classification/entry-points/classificationModalContext';
import { EditorControlsTour } from '@confluence/onboarding-live-doc-changeboarding/entry-points/EditorControlsTour';

import { FullPageComposableEditor } from './AkEditor/ComposableEditor';
import { OnboardingHelpContainer } from './OnboardingHelpContainer';
import {
	ANALYTICS_SOURCE,
	EASY_MENTIONS_PLACEHOLDER_TEXT_ID,
	EASY_MENTIONS_PLACEHOLDER_DEFAULT_MESSAGE,
	AI_PLACEHOLDER_TEXT_ID,
	AI_PLACEHOLDER_DEFAULT_MESSAGE,
} from './fullPageEditorConstants';
import { i18n } from './i18n';
import { useMacroFallbackFlag } from './hooks/useMacroFallbackFlag';
import { useEditorFullPageExperiments } from './hooks/useEditorFullPageExperiments';
import { useStableFunc } from './hooks/useStableFunc';
import { EditorNavigationLockout } from './EditorNavigationLockout';
import { TitleAreaContentComponentsWrapper } from './TitleAreaContentComponentsWrapper';
import { useFullPageEditorLoomInsertion } from './hooks/useFullPageEditorLoomInsertion';
import { useStartLivePageEditExperience } from './hooks/useStartLivePageEditExperience';
import { useEditorContentDisabled } from './hooks/useEditorContentDisabled';
import { useEditorCollabConnectivitySync } from './hooks/useEditorCollabConnectivitySync';
import { useEditorActions } from './hooks/useEditorActions';
import { useFocusedComment } from './hooks/useFocusedComment';
import { FullPageEditorBannerWrapper } from './FullPageEditorBannerWrapper';
import { useFullPageAutoFocus } from './hooks/useFullPageAutoFocus';

export type FullPageEditorComponentProps = NativeCollabInjectedProps & {
	contentId: string;
	editorActions: EditorActions;
	spaceKey: string;
	spaceId: string | undefined;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	/**
	 * defaults to true
	 * used by live pages as live pages render editor on view only mode
	 */
	hasEditPermission: boolean;
	/* supply customPerfData object to add custom properties to all `performance measured` events that use EDITOR_METRIC page segment */
	customPerfData?: Record<string, string | boolean | object>;
	onDraftSyncSuccess?: () => void; // Passed to useEditorDraftSyncAction as a callback when sync is successful.
	loadingPriority?: LoadingPriority | null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullPageEditorWrapper = styled.div({
	isolation: 'isolate',
});

// IMPORTANT: If you change the code below, please also update the
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/pastePluginOptions.ts
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/cardPluginOptions.ts
const CARD_OPTIONS = { allowDatasource: true };

const useLinkCreationQueryParam = createSingleQueryParamHook('linkCreation');
const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useFocusedMentionIdQueryParam = createSingleQueryParamHook('focusedMentionId');
const useReplyToCommentIdQueryParam = createSingleQueryParamHook('replyToComment');

// Content components are displayed below the editor tool bar
const _getContentComponents = memoize(
	({
		contentId,
		debounceTitleChangeUpdates,
		focusEditor,
		onFocusChanged,
		userOffline,
		linkCreation,
		editorPluginAIFeedbackHandlers,
		isAdminHubAIEnabled,
		hideSpinner,
		templateSuggestionsRef,
		templateButtonRef,
		viewMode,
		contentClassificationMetadata,
		spaceKey,
		isUnpublishedDraft,
		showBlankPageQuickActions,
		isLivePage,
		version,
		isEditorEmpty,
		shouldRenderPreview,
		isEditorAnnotationProviderManagersEnabled,
		editorApi,
		contentType,
		editorActions,
		disabled,
		isTitleFocused,
		appearance,
		title,
		onTitleChange,
		cancelDebouncedSaveDraft,
		showSpinner,
		loadingPriority,
		isSpaceOverviewPage,
		isUnreadQueryLoading,
	}) => {
		const titleAreaContentComponents = (
			<TitleAreaContentComponentsWrapper
				isEditorEmpty={isEditorEmpty}
				contentId={contentId}
				focusEditor={focusEditor}
				onFocusChanged={onFocusChanged}
				debounceOnChange={debounceTitleChangeUpdates}
				userOffline={userOffline}
				templateSuggestionsRef={templateSuggestionsRef}
				templateButtonRef={templateButtonRef}
				linkCreation={linkCreation}
				hideSpinner={hideSpinner}
				viewMode={viewMode}
				contentClassificationMetadata={contentClassificationMetadata}
				version={version}
				editorApi={editorApi}
			/>
		);

		let blankPageQuickActionsTemplatePreview;
		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

		const { isBPQAMilestone3ExperimentEnabled } = getBPQAMilestone3ExperimentEnabled({
			shouldFireExperimentExposure: true,
		});

		// show BPQA full size template preview when we
		// are not previewing the editor, BPQA should show,
		// milestone 3 is enabled, and it's either an
		// unpublished draft or a live page
		const shouldShowFullPageTemplatePreview =
			!shouldRenderPreview &&
			showBlankPageQuickActions &&
			isBPQAMilestone3ExperimentEnabled &&
			(isUnpublishedDraft || isLivePage);

		if (shouldShowFullPageTemplatePreview) {
			blankPageQuickActionsTemplatePreview = (
				<BlankPageQuickActionsTemplatePreview spaceKey={spaceKey} />
			);
		}

		const shouldRenderBPQA = !shouldRenderPreview && showBlankPageQuickActions && !isCompanyHub;

		return {
			before: [
				!(isCompanyHub && fg('company_hub_creation_ui')) && titleAreaContentComponents,
				isAdminHubAIEnabled && (
					<EditorPluginAIWithFeedback
						key="editor-ai-feedback-collector"
						editorPluginAIFeedbackHandlers={editorPluginAIFeedbackHandlers}
					/>
				),
				isEditorAnnotationProviderManagersEnabled && (
					<EditorAnnotationProviderComponents key="editorAnnotationProviderComponents" />
				),
				<EditorForgeExtensionConfigHelperRenderer key="editorForgeExtensionConfigHelperRenderer" />,
				!isCompanyHub && (
					<ObjectSidebarControl
						key="object-sidebar-control"
						hasCommentsButton
						hasDetailsButton
						hasLoomButton
						hasAiButton={
							(isLivePage && fg('confluence_ai_uber_button_in_live_pages')) ||
							fg('ai-smart-button-team-25')
						}
						hasReactionsButton={
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							!isSpaceOverviewPage &&
							(isLivePage || !isUnpublishedDraft) &&
							fg('confluence_new_reactions_entry_point_changes') &&
							!fg('confluence-frontend-redesigned-footer-comment')
						}
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
					/>
				),
				shouldRenderBPQA && (
					<BlankPageQuickActions
						editorActions={editorActions}
						disabled={disabled}
						isTitleFocused={isTitleFocused}
						focusEditor={focusEditor}
						appearance={appearance}
						spaceKey={spaceKey}
						isUnpublishedDraft={isUnpublishedDraft}
						title={title}
						onTitleChange={onTitleChange}
						contentId={contentId}
						cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
						showSpinner={showSpinner}
						hideSpinner={hideSpinner}
						isAIEnabled={isAdminHubAIEnabled}
						editorPresetAPI={editorApi}
					/>
				),
				<EditorControlsTour key="onboarding-editor-controls-tour" />,
			],
			after: [
				blankPageQuickActionsTemplatePreview,
				!shouldRenderBPQA && isLivePage && (
					<>
						{!fg('confluence-frontend-redesigned-footer-comment') &&
							fg('confluence_frontend_comments_panel_v2') && (
								<UFOLabel name="editor-footer-comments">
									<PageFooterComments
										key={contentId}
										contentId={contentId}
										loadingPriority={loadingPriority}
									/>
								</UFOLabel>
							)}
						{!isUnreadQueryLoading && fg('confluence-frontend-redesigned-footer-comment') && (
							<UFOLabel name="editor-footer-comments">
								<ObjectCommentWrapper showGeneralComment />
							</UFOLabel>
						)}
					</>
				),
			],
		};
	},
	isEqual,
);

const _getContextPanelComponent = memoize(() => {
	return <EditorContextPanelView />;
});

const _getEditorContextConnectionsPanel = memoize(
	({
		referentialityContext,
		connectionsPanelActiveNodeLocalId,
		setConnectionsPanelActiveNodeLocalId,
		closeConnectionsPanel,
	}) => {
		if (connectionsPanelActiveNodeLocalId && referentialityContext?.api) {
			return (
				<EditorContextConnectionsPanel
					api={referentialityContext.api}
					activeNodeLocalId={connectionsPanelActiveNodeLocalId}
					setActiveNodeLocalId={setConnectionsPanelActiveNodeLocalId}
					closeConnectionsPanel={closeConnectionsPanel}
				/>
			);
		}

		return null;
	},
);

const _getEditorContextSmartButtonConfigPanel = memoize(
	({ smartButtonActiveNodeLocalId, closeSmartButtonConfigPanel, fullPageEditorPresetAPI }) => {
		if (smartButtonActiveNodeLocalId) {
			return (
				<EditorContextSmartButtonConfigPanel
					localId={smartButtonActiveNodeLocalId}
					closeSelf={closeSmartButtonConfigPanel}
					editorApi={fullPageEditorPresetAPI}
				/>
			);
		}
		return null;
	},
	isEqual,
);

type FeatureFlags = { [key: string]: string | number | boolean };

type PlaceholderTextParam = {
	isAIEnabledForContent: boolean;
	featureFlags: FeatureFlags;
};

// IMPORTANT: If you change the code below, please also update the
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/placeholderPluginOptions/placeholderPluginOptions.ts
const _getPlaceholderText = memoize((placeholderTextParam: PlaceholderTextParam) => {
	if (editorExperiment('platform_editor_controls', 'variant1')) {
		return i18n.editorEmptyDocumentPlaceholder;
	}

	const isAIEnabledForContent = placeholderTextParam.isAIEnabledForContent;

	const key = `${isAIEnabledForContent ? 'AI' : 'noAI'}`;

	const placeholders = {
		AI: {
			//  Placeholder text when AI is enabled
			id: AI_PLACEHOLDER_TEXT_ID,
			defaultMessage: AI_PLACEHOLDER_DEFAULT_MESSAGE,
		},
		noAI: {
			//  Placeholder text when AI is not enabled
			id: EASY_MENTIONS_PLACEHOLDER_TEXT_ID,
			defaultMessage: EASY_MENTIONS_PLACEHOLDER_DEFAULT_MESSAGE,
		},
	};

	return placeholders[key];
});

const withEditorContext = (component) =>
	withSmartCardProviderContext(withRevertContext(withEditorModalContext(component)));
const getContentAppearance = memoize((appearance, draftAppearance): FullPageAppearances => {
	// if the appearance is default value (version 0), reference the draftAppearance if it exists; otherwise, use appearance
	const contentAppearance =
		appearance.version === 0 && draftAppearance ? draftAppearance : appearance.appearance;

	return getContentAppearanceForAtlaskit(contentAppearance);
});

const _createLCMExtensionProvider = async (
	outerEditorView: EditorView | undefined,
	outerEditorApi: PublicPluginAPI<[ExtensionPlugin, LegacyContentMacroPlugin]> | undefined,
) => {
	const { createManifest } = await import(
		/* webpackChunkName: "loadable-legacy-content-macro" */ '@confluence/experiment-legacy-content-macro'
	);

	return new DefaultExtensionProvider([
		createManifest(outerEditorView, outerEditorApi) as unknown as ExtensionManifest,
	]);
};

export const FullPageEditorComponent = withDocChangeContext(
	withEditorContext(
		withEditorActions(
			withFlags(
				withDialogs(
					(
						props: FullPageEditorComponentProps &
							WithFlagsProps &
							WithDialogsProps &
							WithDocChangeContextProps,
					) => {
						const {
							// Props from Loader
							contentId,
							spaceKey,
							collabEditProvider,
							collabEdit,
							collabEditState,
							heartbeatData,
							contentType,
							disabled,
							setForceDisabled,
							internetDisconnected,
							forceDisabled,
							draftShareId,
							refetchNativeCollab,
							isFirstParticipant,
							isSingleUserSession,
							isUnpublishedDraft,
							onTitleChange,
							title,
							version,
							viewMode,
							onPublishUpdateTitleInPageTree,
							customPerfData,
							// Props from editor-core
							editorActions,
							// Context Provider for macro
							extensionContextProvider,
							flags,
							dialogs,
							handleDocChange,
							/**
							 * defaults to true
							 * used by live pages as live pages render editor on view only mode
							 */
							hasEditPermission = true,
							createdDate,
							onDraftSyncSuccess,
							loadingPriority,
						} = props;

						const [{ fullPageEditorPresetAPI }, { setEditorApi, resetEditorApi }] =
							useFullPageEditorApi();

						// Ensures editorApi sweet state is cleared on unmount
						useEffect(() => {
							return () => {
								resetEditorApi();
							};
						}, [resetEditorApi]);

						// Recommended way of accessing collabEditProvider without promise wrapper.
						const [{ collabEditProvider: extractedCollabEditProvider }] = useNativeCollabState();
						const [{ collabEditConnected }] = useNativeCollabConnected();
						const spaceId = useSpaceId();

						const homepageId = useSpaceHomepageId(spaceKey);
						const isSpaceOverviewPage = homepageId === contentId;

						const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
							contentId,
							contentType,
							subject: 'editPage',
							source: ANALYTICS_SOURCE,
							version,
							collabService: 'ncs',
						});

						const { requestToEditContent, hasRequestedEditPermission } = useRequestToEditContent({
							contentId,
							flags,
							triggerAnalyticsEvent,
						});

						const isClassicMentionsNotificationsEnrolled =
							useIsMentionOnRestrictedPageEligible() &&
							expValNoExposure(
								'cc_onboarding_mention_on_restricted_page',
								'cohort',
								'not-enrolled',
							) !== 'not-enrolled';

						const { setEditorAnnotations } = useEditorAnnotationsActions();
						const { setMode: setLivePageMode } = useLivePageStoreActions();
						// TODO useSharedPluginStateSelector would be preferred here, but it doesn't currently work
						// from the FullPageEditorComponent
						// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
						// @ts-expect-error
						const { editorViewModeState } = useSharedPluginState(fullPageEditorPresetAPI as any, [
							'editorViewMode' as never,
						]);
						const isConsumption = editorViewModeState?.isConsumption;
						usePluginStateEffect(
							fullPageEditorPresetAPI as any,
							['editorViewMode'],
							({ editorViewModeState }) => {
								if (!editorExperiment('live_pages_graceful_edit', 'control')) {
									if (editorViewModeState?.isConsumption === false) {
										void setLivePageMode('edit');
									}
								}
							},
						);

						const { prioritySortingFn, handleInsert } = useSmartElementRecommendations();
						const quickInsertOptions = useMemo(
							() => ({
								prioritySortingFn,
								onInsert: handleInsert,
							}),
							[prioritySortingFn, handleInsert],
						);

						const isDocumentActive = useDocumentActive();

						const { isSmartButtonAllowed } = useSmartButtonPremiumGate();

						const { updateTitle: updateContentTypesTitle } = useContentTypesTitleUpdate(
							contentId,
							title || '',
						);

						const {
							collabEditInitialized,
							collabEditError,
							collabEditInternetLost,
							collabEditConnectionTimedOut,
						} = collabEditState;
						const intl = useIntl();
						const {
							cloudId,
							userId,
							featureFlags,
							isAdminHubAIEnabled,
							edition,
							orgId,
							environment,
						} = useSessionData();
						const { push } = useRouteActions();
						const linkCreation = useLinkCreationQueryParam();
						const focusedCommentId = useFocusedCommentIdQueryParam();

						const replyToCommentId = useReplyToCommentIdQueryParam();
						const focusedMentionId = useFocusedMentionIdQueryParam();
						const { createAnalyticsEvent } = useAnalyticsEvents();
						const { triggerSaveIndicator } = useContext(TriggerSaveIndicatorContext);

						const isEditorEmpty =
							// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
							// @ts-expect-error
							useSharedPluginState(fullPageEditorPresetAPI, ['empty'])?.emptyState?.editorIsEmpty ??
							editorActions?.isDocumentEmpty();

						const showBlankPageQuickActions = useShouldShowBlankPageQuickActions({
							contentType,
							collabEditInitialized,
							isEditorEmpty,
							viewMode,
						});

						const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();

						const isLivePage = useIsCurrentPageLive();

						const templateSuggestionsRef = useRef<HTMLDivElement | null>(null);
						const templateButtonRef = useRef<HTMLElement | null>(null);

						const [{ editSource }] = useEditSource();

						const experienceTracker = useContext(ExperienceTrackerContext);

						const handleGqlError = useCallback(
							(error: Error) => {
								experienceTracker.stopOnError({
									name: VIEW_INLINE_COMMENT_EXPERIENCE,
									error,
									attributes: {
										mode: isLivePage ? InlineCommentMode.LIVE : InlineCommentMode.EDIT,
									},
								});
							},
							[experienceTracker, isLivePage],
						);

						const [isInitialRender, setIsInitialRender] = useState(true);

						useExposeGoogleMeetScreenShare();

						// === Editor State ===
						const [shouldRenderPreview, setShouldRenderPreview] = useState<boolean>(false);
						const [previewAsBlog, setPreviewAsBlog] = useState<boolean>(false);
						const [stagedContentId, setStagedContentId] = useState<string | undefined>();
						const [stagedSpaceKey, setStagedSpaceKey] = useState<string | undefined>();
						const [proseMirrorRenderedData, setProseMirrorRenderedData] =
							useState<ProseMirrorRenderedDataType | null>(null);
						const [checkIfConditionPreventingPublish, setCheckIfConditionPreventingPublish] =
							useState<() => Promise<boolean>>();
						const assignConditionalFunction = useCallback(
							(conditionalFunction: any) =>
								setCheckIfConditionPreventingPublish(() => conditionalFunction),
							[],
						);
						const { isUnreadQueryLoading } = useUnreadCommentsState();

						const { updateUnresolvedInlineComment } = useInlineCommentsActions();
						const { setOrderedActiveAnnotationIdList, clearCommentsData } =
							useCommentsDataActions();
						const { resetInitialDataLoadedMap } = useCommentsPanelActions();
						const { updateUnreadComments, updateUnreadCommentsOnFirstRender } =
							useUnreadCommentsActions();
						const { hideObjectSidebar } = useObjectSidebarActions();
						const isObjectSidebarShown = useIsObjectSidebarShown();

						useScrollOnUrlChange(proseMirrorRenderedData !== null);

						const handleCollabError = useHandleCollabError(flags);
						const { data: onboardingStateData, loading: onboardingStateLoading } =
							useGetOnboardingState(
								Object.values(EDITOR_ONBOARDING_STATE_KEYS),
								!fg('cc_onboarding_experience') || version !== 1,
							);

						// when a user is eligible for contextual invite nudge, tag them before they publish
						void useContextualInviteNudge();

						const { editorOnboardingEligiblity, recentSpotlightCompleted } =
							deserializeState(onboardingStateData);

						const shouldShowRecentSpotlight =
							!onboardingStateLoading && editorOnboardingEligiblity && !recentSpotlightCompleted; // Makes sure recentSpotlightCompleted is not undefined

						const { onEditorReady, startingLinks } = useInviteFromLinkExperimentEditor();

						// === Editor common functionalities and providers ===
						const { collabLimitReached, heartbeatError, stopHeartbeatCallback, editorSessionId } =
							heartbeatData;

						const {
							cancelDebouncedSaveDraft,
							triggerSaveDraft,
							navigateOnClose,
							onPublish,
							isPublishingContent,
							saveDraft,
							saveDraftError,
						} = useDraftSyncAndPublish({
							contentId,
							contentType,
							refetchNativeCollab,
							isFirstParticipant,
							isUnpublishedDraft,
							spaceKey,
							title,
							version,
							onCollabError: handleCollabError,
							editorActions,
							checkIfConditionPreventingPublish,
							flags,
							dialogs,
							editorPresetAPI: fullPageEditorPresetAPI,
							editorSessionId,
							onPublishUpdateTitleInPageTree,
							startingLinks,
						});

						const { draftSyncActionError } = useEditorDraftSyncAction({
							contentId,
							editorPresetAPI: fullPageEditorPresetAPI,
							editorActions,
							onDraftSyncSuccess,
							isFirstParticipant,
						});

						const [isAutoConverted] = useAutoConvertState();

						const { draftTitle, draftBody, draftAppearance } = useInjectDraftContent({
							contentId,
							draftShareId,
							collabEditInitialized,
							editorActions,
						});

						const {
							connectionsPanelActiveNodeLocalId,
							setConnectionsPanelActiveNodeLocalId,
							openConnectionsPanel,
							closeConnectionsPanel,
						} = useConnectionsPanel();

						const {
							activeNodeLocalId,
							openConfigPanel,
							closeConfigPanel,
							upsellModalSource,
							setUpsellModalSource,
						} = useCustomExtensionConfigPanel(editorActions);

						const {
							smartButtonActiveNodeLocalId,
							openSmartButtonConfigPanel,
							closeSmartButtonConfigPanel,
						} = useSmartButtonConfigPanel();

						const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

						const { optInStatus, triggerOptInFlow, AIOptInModal } = useEditorAIOptIn({
							isAdminHubAIEnabled,
							isAIEnabledForContent,
							edition,
							orgId,
							environment,
						});

						const aiGlobalOptIn = useMemo(
							() => ({
								status: optInStatus,
								triggerOptInFlow,
							}),
							[optInStatus, triggerOptInFlow],
						);

						const { editorPluginAIFeedbackHandlers } = useEditorPluginAIFeedbackHandlers();
						const { onAIProviderChanged } = useEditorPluginAICollabConnector();

						const getChannelVariables = useGetChannelVariables(cloudId, userId || 'anonymous');

						const mediaOptions = useMediaOptions({
							contentId,
							contentType,
							flags,
							viewMode,
							editorAPI: fullPageEditorPresetAPI ?? undefined,
						});

						const handleFeedbackClicked = useCallback(
							(props) => {
								editorPluginAIFeedbackHandlers.current.handleFeedbackClicked(props);
							},
							[editorPluginAIFeedbackHandlers],
						);

						const handleReviewStateEntered = useCallback(() => {
							editorPluginAIFeedbackHandlers.current.handleReviewStateEntered();
						}, [editorPluginAIFeedbackHandlers]);

						const handleExperienceEnded = useCallback(() => {
							editorPluginAIFeedbackHandlers.current.handleExperienceEnded();
						}, [editorPluginAIFeedbackHandlers]);

						const editorPluginAIProviders = useMemo(
							() => ({
								mediaProvider: mediaOptions.provider!,
							}),
							[mediaOptions.provider],
						);

						const editorPluginAIOptions = useEditorPluginAIOptions({
							aiGlobalOptIn,
							appearance: 'full-page',
							onFeedbackClicked: handleFeedbackClicked,
							onReviewStateEntered: handleReviewStateEntered,
							onExperienceComplete: handleExperienceEnded,
							onAIProviderChanged,
							getChannelVariables,
							providers: editorPluginAIProviders,
							contentId,
						});

						useSendContentAgeAnalytics({ contentId, collabService: 'ncs' });

						const {
							isTitleFocused,
							shouldFocusEditor,
							debounceTitleChangeUpdates,
							focusEditor,
							onFocusChanged,
						} = useEditorTitleFocus({
							contentType,
							editorActions,
							isUnpublishedDraft,
						});

						// Editor Disabled Analytics
						useDisabledAnalytics({
							contentId,
							disabled: isTitleFocused || disabled,
							shouldRenderPreview,
							isTitleFocused,
							collabEditError,
							collabLimitReached,
							heartbeatError,
						});

						const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
							updateUnresolvedInlineComment,
							setOrderedActiveAnnotationIdList,
						]);
						const annotationProviderOptions = useEditorAnnotationProviderOptions({
							pageId: contentId,
							triggerAnalyticsEvent,
							editorActions,
							updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
								? updateUnresolvedInlineCommentFn
								: updateUnresolvedInlineComment,
						});
						const activityProvider = useActivityProvider();
						const mentionProvider = useMentionProvider(spaceId, undefined, focusedMentionId);
						const profilecardProvider = useProfilecardProvider({
							push,
							cloudId,
							featureFlags,
							userId,
						});
						const mentionProps = useMemo(() => {
							return {
								profilecardProvider,
							};
						}, [profilecardProvider]);
						const searchProvider = useSearchProvider();
						const findReplaceOptions = getFindReplaceOptions();
						const taskDecisionProvider = getTaskDecisionProvider();
						const autoformattingProvider = useAutoformattingProvider({
							contentId,
							spaceKey,
							source: ANALYTICS_SOURCE,
						});

						const contextIdentifierProvider = useContextIdentifierProvider(spaceId, contentId);

						const { showMacroFallbackFlag } = useMacroFallbackFlag(flags, isLivePage);
						const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

						const { isLivePagesFeatureEnabled, livePageReleasePhase } =
							useIsLivePagesFeatureEnabled();

						// Extension V2 API props
						const { extensionOptions, rendererExtensionOptions } = useExtensionOptions({
							contentId,
							contentType,
							editorActions,
							mentionProvider,
							spaceKey,
							spaceId,
							extensionContextProvider,
							openConnectionsPanel,
							openConfigPanel,
							openSmartButtonConfigPanel,
							onCompleteCallback: showMacroFallbackFlag,
							isLivePage,
							allowCreateDatabaseExtension: true,
							allowDatabaseQuickInsert: true,
							allowCreateWhiteboardExtension: true,
							flags,
							aiOptInStatus: optInStatus,
							editorAPI: fullPageEditorPresetAPI ?? undefined,
							setUpsellModalSource,
							isFullPageEditor: true,
							allowCompanyHubExclusiveExtensions: isCompanyHub,
							isLivePagesFeatureEnabled,
						});

						const extensionProvidersWithLCM = useMemo(() => {
							const outerEditorView = editorActions._privateGetEditorView();

							const LCMExtension = _createLCMExtensionProvider(
								outerEditorView,
								fullPageEditorPresetAPI,
							);

							return [...extensionOptions.extensionProviders, LCMExtension];
						}, [editorActions, fullPageEditorPresetAPI, extensionOptions.extensionProviders]);

						const { showRevertDialog } = useEditorRevertDialogContext();
						const { showSpinner, hideSpinner, shouldShowSpinner } = useSpinner();

						const editorAnnotationsRef = useRef<string[]>([]);
						const annotationStateRef = useRef<InlineCommentPluginState | undefined>();
						const { removeCommentQueryParams } = useInlineCommentQueryParams();

						usePluginStateEffect(
							fullPageEditorPresetAPI as any,
							['annotation'],
							({ annotationState }) => {
								const annotations = annotationState?.annotations;
								const { selectedAnnotations, mouseData } = annotationState || {};
								annotationStateRef.current = annotationState;

								const handleCommentQueryParams = () => {
									if (
										selectedAnnotations &&
										selectedAnnotations.length > 0 &&
										mouseData?.isSelecting &&
										(focusedCommentId || replyToCommentId)
									) {
										removeCommentQueryParams();
									}
								};

								const setAnnotations = () => {
									const editorAnnotationsToSet = annotations ? Object.keys(annotations) : [];
									setEditorAnnotations(getSortedMarkerRefs(editorAnnotationsToSet, 'edit'));
									editorAnnotationsRef.current = editorAnnotationsToSet;
									const activeEditorAnnotationsToSet = annotations
										? Object.keys(annotations).filter((key) => annotations[key] === false)
										: [];
									setOrderedActiveAnnotationIdList(
										getSortedMarkerRefs(activeEditorAnnotationsToSet, 'edit'),
									);
								};

								handleCommentQueryParams();
								setAnnotations();
							},
						);

						useFocusedComment({
							commentId: focusedCommentId || replyToCommentId,
							contentId,
							handleGqlError,
							annotationState: annotationStateRef.current,
						});

						useEditorInlineCommentsPubSub({
							contentId,
							contentType,
							triggerAnalyticsEvent,
							updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
								? updateUnresolvedInlineCommentFn
								: updateUnresolvedInlineComment,
							isDocumentActive,
						});

						const { retrieveEditorTitleAndContent } = useRetrieveEditorTitleAndContent({
							onCollabError: handleCollabError,
							editorActions,
						});

						useCommentRefetch({
							isDocumentActive,
							previousAnnotations: editorAnnotationsRef.current,
							isInitialRender,
							setIsInitialRender,
							contentId,
							triggerAnalyticsEvent,
							updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
								? updateUnresolvedInlineCommentFn
								: updateUnresolvedInlineComment,
							handleGqlError,
							updateUnreadComments,
							updateUnreadCommentsOnFirstRender,
						});

						// TODO: add missing configs/providers (possibly not all):
						// presenceProvider
						// legacyImageUploadProvider
						// placeholderBracketHint
						// allowAnalyticsGASV3
						// onDestroy

						const wrapperProps: EditorWrapperProps = {
							attribution: Attribution.PAGE_EXTENSIONS,
							collabEditState,
							contentId,
							forceDisableEditor: setForceDisabled,
							isUnpublishedDraft,
							contentType,
							editorActions,
							heartbeatError,
							saveDraftError: isLivePage ? draftSyncActionError : saveDraftError,
							stopHeartbeatCallback,
							isLivePage,
							editorDisabled: disabled,
						};

						const performanceTrackingOptions = undefined;

						const editorFeatureFlags = useEditorFeatureFlags();
						const editorExperiments = useEditorFullPageExperiments();

						useConnecting(extractedCollabEditProvider);

						// Fetch content's classification data

						const contentClassificationMetadata = useContentClassification({
							contentId,
							spaceKeyOverride: spaceKey,
							contentStatusContext: isLivePage ? 'current-and-draft' : 'draft-only',
						});

						useEffect(() => {
							if (collabEditInitialized && !disabled) {
								scrollToLastViewedPosition(contentId);
							}
						}, [collabEditInitialized, disabled, contentId]);

						useEffect(() => {
							// DTR-1401-CFE
							// HOT-103036

							// If a blogpost is selected from in-editor templates panel, any preset restrictions are lost.
							// This lets the user know to update their blog restrictions.
							const showBlogpostAccessFlag = localStorage.getItem(keys.BLOGPOST_PERMISSIONS_NOTICE);

							if (flags && showBlogpostAccessFlag === 'true') {
								void flags.showInfoFlag({
									title: <FormattedMessage {...i18n.blogpostPermissionsNotice} />,
								});
								localStorage.removeItem(keys.BLOGPOST_PERMISSIONS_NOTICE);
							}
						}, [flags]);

						useEffect(() => {
							document.addEventListener('keydown', () => measureTimeToEdit(triggerAnalyticsEvent), {
								once: true,
							});
						}, [triggerAnalyticsEvent]);

						// Close the object sidebar when navigating to a new page
						useEffect(() => {
							hideObjectSidebar();
						}, [contentId, hideObjectSidebar]);

						useEffect(() => {
							return () => {
								if (fg('confluence-frontend-comments-panel')) {
									resetInitialDataLoadedMap();
									clearCommentsData();
								}
							};

							// eslint-disable-next-line react-hooks/exhaustive-deps
						}, [contentId]);

						// Focus the main content area for live pages Full Page Editor mount.
						useFullPageAutoFocus(isLivePage);

						const onProseMirrorRendered = (proseMirrorRenderedAttributes) => {
							proseMirrorRenderedAttributes = proseMirrorRenderedAttributes || {};
							// "distortedDuration" gets a true value if page was loaded in the background
							// in a non-visible tab which can lead to inaccurate duration numbers,
							// this can help us to capture only legitimate durations in our metric dashboards.
							// See https://bitbucket.org/atlassian/atlassian-frontend/src/master/packages/editor/editor-common/src/utils/performance/measure-render.ts
							const {
								proseMirrorRenderedFMP,
								proseMirrorRenderedTimestamp,
								startTime,
								duration,
								nodes,
								distortedDuration,
							} = proseMirrorRenderedAttributes;
							setProseMirrorRenderedData({
								proseMirrorRenderedFMP,
								proseMirrorRenderedTimestamp,
								startTime,
								duration,
								nodes,
								distortedDuration,
							});
						};
						useEffect(() => {
							waitForGlobal(
								{
									listener: 'FullPageEditorComponent',
									globalProperty: 'AJS.bind',
								},
								() => {
									(window as any).AJS.bind('createGASv3Event', (_, data) => {
										createAnalyticsEvent(data).fire();
									});
								},
							);

							return () => {
								waitForGlobal(
									{
										listener: 'FullPageEditorComponent',
										globalProperty: 'AJS.unbind',
									},
									() => {
										(window as any).AJS.unbind('createGASv3Event');
									},
								);
							};
						}, [createAnalyticsEvent]);

						useEffect(() => {
							isAutoConverted &&
								sendEditorConversionAnalyticsPageEvent(
									createAnalyticsEvent,
									{
										pageAction: AutoConvertEvent.EDITED,
										source: 'FullPageEditorComponent-Next',
									},
									AutoConvertActionSubject.AUTO_CONVERSION,
								);
						}, [isAutoConverted, createAnalyticsEvent]);

						const { setOptimisticTitle, optimisticTitle } = useOptimisticTitleChange(
							contentId,
							title,
						);
						const handleTitleChange = useStableFunc((newTitle: string) => {
							if (isLivePage) {
								/*
				Live pages immediately persist title changes to the backend and change the page title in the UI,
				unlike regular pages where changing the title only alters the draft version of the page and
				it is not fully persisted until the user saves the draft.
				*/
								updateContentTypesTitle(newTitle);
								return;
							}

							setOptimisticTitle(newTitle);

							triggerSaveIndicator();
							onTitleChange(newTitle);
							triggerSaveDraft();
						});

						useStartLivePageEditExperience({
							editorApi: fullPageEditorPresetAPI || undefined,
							contentId,
						});

						const { openPublishModal } = usePublishModalMountActions();
						const validatePageAndOpenModal = usePrePublishTitleValidation({
							contentId,
							contentType,
							isUnpublishedDraft,
							flags,
							retrieveEditorTitleAndContent,
							onBeforeValidation: useCallback(() => {
								void saveDraft?.();
							}, [saveDraft]),
							onValidationSuccess: openPublishModal,
							analyticsSource: 'LivePagesDetailsPanel',
							optimisticTitle,
						});

						useEditorAIReplacingTitle(isAIEnabledForContent, handleTitleChange);

						const handleBreadcrumbsClick = useCallback(
							(destinationUrl: string) => {
								// https://product-fabric.atlassian.net/browse/CEMS-2479
								// sync draft and content title with NCS
								// when user click on breadcrumbs to exit the editor
								// so title is up-to-date in page tree
								return new Promise<void>((resolve) => {
									void saveDraft({
										isOnClose: true,
									}).then(() => {
										push(destinationUrl);
										resolve();
									});
								});
							},
							[push, saveDraft],
						);

						// [ED-27497] IMPORTANT: This has been moved to the below function. If you add anything here, also add it to the new function.
						// `quickInsertPluginOptions` function in 'packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/quickInsertPluginOptions.ts'.
						const elementBrowser = useMemo(
							() => ({
								showModal: true,
								replacePlusMenu: true,
								emptyStateHandler: getMarketplaceAppsContainer,
							}),
							[],
						);

						// IMPORTANT: If you add a new feature flag here, please make sure to add it to the
						// `featureFlagsPluginOptions` function in 'packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/featureFlagsPluginOptions.ts`.
						const fullPageEditorFeatureFlags = useMemo(
							() => ({
								...editorFeatureFlags,
								...editorExperiments,
								...{
									'basic-text-transformations':
										// utilise this flag as part of eligibility check to avoid passing AI enablement status as editor prop
										aiGlobalOptIn.status === 'disabled',
								},
							}),
							[editorFeatureFlags, editorExperiments, aiGlobalOptIn.status],
						);

						const onPreviewClicked = useCallback(
							(requestingPreviewAsBlog: any, stagedMoveParams: any) => {
								setPreviewAsBlog(requestingPreviewAsBlog === true);
								setShouldRenderPreview(true);
								setStagedContentId(stagedMoveParams?.newParentPageId);
								setStagedSpaceKey(stagedMoveParams?.newSpaceKey);
							},
							[setPreviewAsBlog, setShouldRenderPreview, setStagedContentId, setStagedSpaceKey],
						);

						const onClickPublish = useStableFunc(onPublish);
						const navigateOnCloseStable = useStableFunc(navigateOnClose);

						useSchedulePublishFlagsWatcher({
							contentId,
							contentType,
							isUnpublishedDraft,
							title: title ?? draftTitle,
							flags,
							isSchedulePublishEnabled: !isLivePage,
						});

						const { cohort } = getBlankPageQuickActionsExperimentEnabled({
							shouldFireExperimentExposure: false,
						});

						// Insert Loom video at top of editor page if a Loom url exists
						useFullPageEditorLoomInsertion(fullPageEditorPresetAPI);

						const { isExternalCollaborator } = useIsExternalCollaborator();

						const shouldShowEditorDistinctionBanner =
							!isLivePage &&
							!isUnpublishedDraft &&
							!isCompanyHub &&
							livePageReleasePhase === LivePageReleasePhase.AB_TEST_OPTED_IN;

						const { getViewPagePath } = useHeadingLinksPathBuilder({
							contentId,
							spaceKey,
							contentType,
							title,
							createdDate,
						});

						const editorDisabled = useEditorContentDisabled({
							disabled,
							collabEditInternetLost,
							internetDisconnected,
						});

						useEditorCollabConnectivitySync({
							collabEditInternetLost,
							internetDisconnected,
							editorFullPagePresetAPI: fullPageEditorPresetAPI,
						});

						const extensionRendererViewOptions = useExtensionRendererViewOptions({
							getExtensionHandlers: rendererExtensionOptions?.getExtensionHandlers,
							isLivePage,
							contentId,
							spaceKey,
						});

						const { isDesktop } = useIsDesktop();

						const userOffline = collabEditInternetLost || collabEditConnectionTimedOut;

						const finalExtensionProviders = extensionProvidersWithLCM;

						const ssrExtensionHandlers = useEditorSSRExtensionHandlers(
							contentId,
							spaceKey,
							finalExtensionProviders,
						);

						const FullPageEditorComponentWrapper =
							isCompanyHub && fg('company_hub_creation_ui')
								? HubEditorWrapper
								: FullPageEditorWrapper;

						const { canUndo, canRedo, undo, redo, activateFindReplace } =
							useEditorActions(fullPageEditorPresetAPI);

						const editorActionsProps =
							editorExperiment('platform_editor_controls', 'variant1') &&
							fg('platform_editor_controls_move_actions')
								? {
										canUndo,
										canRedo,
										undo,
										redo,
										activateFindReplace,
									}
								: undefined;

						return (
							<UFOLabel name="full-page-editor-component">
								<EditorDatabasesOnboarding editorApi={fullPageEditorPresetAPI}>
									<SaveIndicatorContextProvider editorApi={fullPageEditorPresetAPI}>
										<EditorContextPanelContextProvider editorAPI={fullPageEditorPresetAPI}>
											<ClassificationModalContextProvider>
												<EditorContextContentCreationPanelWrapper
													cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
													collabEditInitialized={collabEditInitialized}
													contentId={contentId}
													contentType={contentType}
													draftShareId={draftShareId}
													editorActions={editorActions}
													hideSpinner={hideSpinner}
													isUnpublishedDraft={isUnpublishedDraft}
													renderPanel={!shouldRenderPreview}
													showSpinner={showSpinner}
													title={title}
												>
													{({
														isContentCreationPanelOpen,
														onClickOpenContentCreationPanel,
														beforeUpdateRequest,
														postUpdateRequest,
													}) => (
														<ContentAppearanceContextProvider contentId={contentId}>
															<ContentAppearanceContextConsumer>
																{({ contentAppearance }) => {
																	let appearance: FullPageAppearances = getContentAppearance(
																		contentAppearance,
																		draftAppearance,
																	);

																	if (isCompanyHub) {
																		// Company Hub editors no longer have the option of changing page width,
																		// as content topper is removed. For users who had previously set Company
																		// Hub page to 'full-width' and can no longer change it back, we force it to
																		// 'full-page' to fix unexpected layout issues and allow editors to
																		// set the width on individual extension instances.
																		appearance = 'full-page';
																	}
																	const referentialityContext = getReferentialityContext(
																		createAnalyticsEvent,
																		contentId,
																		appearance,
																		ssrExtensionHandlers,
																		extensionRendererViewOptions,
																	);

																	// IMPORTANT: If you change the code below, please also update the
																	// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/placeholderPluginOptions/placeholderPluginOptions.ts
																	const placeholderText =
																		!viewMode &&
																		_getPlaceholderText({
																			featureFlags,
																			isAIEnabledForContent,
																		});

																	return (
																		<FullPageEditorComponentWrapper>
																			<BlankPageQuickActionsProvider
																				cohort={cohort}
																				spaceKey={spaceKey}
																			>
																				<HeadingLinksStyleWrapper>
																					<EditorWrapper {...wrapperProps}>
																						{isLivePage && (
																							<>
																								<PublishModalMountPoint
																									onClickPublish={onClickPublish}
																									onClickPreview={onPreviewClicked}
																									retrieveEditorTitleAndContent={
																										retrieveEditorTitleAndContent
																									}
																									publishButtonDisabled={false}
																									title={title ?? draftTitle}
																									userViewingPreview={false}
																									isUnpublishedDraft={false}
																									contentType={contentType}
																									shouldShowWorldIcon={false}
																									shouldShowFooterButtons={false}
																									isConvertToPageDialog
																								/>
																								<PublishModalKeyboardShortcuts
																									contentId={contentId}
																									contentType={contentType}
																									isUnpublishedDraft={isUnpublishedDraft}
																									hasEditPermission={hasEditPermission}
																									flags={flags}
																									retrieveEditorTitleAndContent={
																										retrieveEditorTitleAndContent
																									}
																									saveDraft={saveDraft}
																								/>
																							</>
																						)}
																						{editorExperiment(
																							'platform_editor_controls',
																							'variant1',
																						) ? (
																							<FullPageEditorBannerWrapper
																								isLivePage={isLivePage}
																								isEmbeddedConfluence={isEmbeddedConfluence}
																								viewMode={viewMode}
																								hasEditPermission={hasEditPermission}
																								shouldRenderPagePreview={shouldRenderPreview}
																								spaceKey={spaceKey}
																								isUnpublishedDraft={isUnpublishedDraft}
																								isCompanyHub={isCompanyHub}
																								isABTestOptedInLivePageReleasePhase={
																									livePageReleasePhase ===
																									LivePageReleasePhase.AB_TEST_OPTED_IN
																								}
																								editorFullPagePresetAPI={fullPageEditorPresetAPI}
																								triggerAnalyticsEvent={triggerAnalyticsEvent}
																							/>
																						) : (
																							<>
																								{isLivePage
																									? !fg(
																											'confluence_live_pages_ab_test_opted_in',
																										) && (
																											<LivePagesFeedbackBannerLoader
																												isViewOnly={viewMode && !hasEditPermission}
																											/>
																										)
																									: !shouldRenderPreview && (
																											<CompanyHubBannerLoader spaceKey={spaceKey} />
																										)}
																								{shouldShowEditorDistinctionBanner && (
																									<EditorDistinctionBanner />
																								)}
																							</>
																						)}
																						{!isEmbeddedConfluence &&
																							(fg('confluence_frontend_object_header') ? (
																								<EditorObjectHeaderContextProvider
																									value={{
																										saveDraft,
																										navigateOnClose,
																										triggerAnalyticsEvent,
																										version,
																										editorActions,
																										title,
																										onClickOpenContentCreationPanel,
																										isContentCreationPanelOpen,
																										retrieveEditorTitleAndContent,
																										showRevertDialog,
																										onPreviewClicked,
																										hasEditPermission,
																										isPublishingContent,
																										userOffline,
																										forceDisabled,
																										editorTitle: title ?? draftTitle,
																										collabLimitReached,
																										collabEditConnected,
																										onClickPublish,
																										shouldRenderPreview,
																										isUnpublishedDraft,
																										validatePageAndOpenModal,
																										...editorActionsProps,
																									}}
																								>
																									<ExperienceStart
																										name={OBJECT_HEADER_EXPERIENCE}
																									/>
																									<ContentTypesHeader
																										contentId={contentId}
																										collabEditOption={collabEdit}
																									/>
																								</EditorObjectHeaderContextProvider>
																							) : (
																								<EditorActionBar
																									spaceKey={spaceKey}
																									editorActions={editorActions}
																									collabEditState={collabEditState}
																									title={title ?? draftTitle}
																									forceDisabled={forceDisabled}
																									userOffline={userOffline}
																									contentId={contentId}
																									contentType={contentType}
																									isContentCreationPanelOpen={
																										isContentCreationPanelOpen
																									}
																									isUnpublishedDraft={isUnpublishedDraft}
																									onClickPublish={onClickPublish}
																									onClickOpenContentCreationPanel={
																										onClickOpenContentCreationPanel
																									}
																									onPreviewClicked={onPreviewClicked}
																									saveDraft={saveDraft}
																									collabLimitReached={collabLimitReached}
																									showRevertDialog={showRevertDialog}
																									retrieveEditorTitleAndContent={
																										retrieveEditorTitleAndContent
																									}
																									isPublishingContent={isPublishingContent}
																									isSingleUserSession={isSingleUserSession}
																									navigateOnClose={navigateOnCloseStable}
																									triggerAnalyticsEvent={triggerAnalyticsEvent}
																									shouldRenderPreview={shouldRenderPreview}
																									shouldShowRecentSpotlight={
																										shouldShowRecentSpotlight
																									}
																									checkIfConditionPreventingPublish={
																										checkIfConditionPreventingPublish
																									}
																									flags={flags}
																									createAnalyticsEvent={createAnalyticsEvent}
																									collabEditOption={collabEdit}
																									hasEditPermission={hasEditPermission}
																									editorDisabled={disabled}
																									isTitleFocused={isTitleFocused}
																									viewMode={viewMode}
																									contentClassificationMetadata={
																										contentClassificationMetadata
																									}
																									isLivePage={isLivePage}
																									requestToEditContent={requestToEditContent}
																									hasRequestedEditPermission={
																										hasRequestedEditPermission
																									}
																									ellipsisEditorActions={editorActionsProps}
																								/>
																							))}
																						<EditorDisplayWrapper
																							data-testid="full-page-editor-style"
																							//TODO: Control isHidden prop
																							isHidden={shouldRenderPreview}
																							isEditorActionBarRendered
																						>
																							{isDesktop ||
																							editorExperiment(
																								'platform_editor_offline_editing_web',
																								true,
																							) ? (
																								<EditorConnectionBannerOfflineMode
																									editorType="next"
																									triggerAnalyticsEvent={triggerAnalyticsEvent}
																									collabEditState={collabEditState}
																									willEditorDisable={!editorDisabled}
																									contentType={contentType}
																									contentId={contentId}
																									spaceId={spaceId}
																									fullPageEditorPresetAPI={fullPageEditorPresetAPI}
																									viewMode={viewMode}
																									isLivePage={isLivePage}
																								/>
																							) : (
																								<EditorConnectionBanner
																									editorType="next"
																									triggerAnalyticsEvent={triggerAnalyticsEvent}
																									collabEditInternetLost={collabEditInternetLost}
																									collabEditConnectionTimedOut={
																										collabEditConnectionTimedOut
																									}
																									willEditorDisable={disabled}
																									contentType={contentType}
																									contentId={contentId}
																									viewMode={viewMode}
																								/>
																							)}
																							<OnboardingHelpContainer
																								contentId={contentId}
																								isContentCreationPanelOpen={
																									isContentCreationPanelOpen
																								}
																								onClickOpenContentCreationPanel={
																									onClickOpenContentCreationPanel
																								}
																								editorActions={editorActions}
																								isShowingOfflineBanner={collabEditInternetLost}
																								version={version}
																								spaceKey={spaceKey}
																								editorApi={fullPageEditorPresetAPI}
																								handleTitleChange={handleTitleChange}
																							/>
																							{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
																							{!process.env.REACT_SSR && (
																								<ConfluenceRecapModalLoadable
																									isUnpublishedDraft={isUnpublishedDraft}
																									onClickPublish={() =>
																										onPublish({
																											suppressNotifyWatchers: false,
																										})()
																									}
																								/>
																							)}
																							<PageLoadMark name="onBeforeEditor" />
																							<MacroExperienceStart
																								contentId={contentId}
																								mode={EDITOR}
																								name={VIEW_ALL_MACRO_EXPERIENCE}
																								timeout={ExperienceTimeout.MACRO_LOAD}
																							/>
																							{fg('confluence_footer_comment_styling_change') && (
																								<PageCommentsPubSubHandler
																									contentId={contentId}
																									contentType={contentType}
																								/>
																							)}
																							<EditorAnalyticsListener
																								contentId={contentId}
																								contentType={contentType}
																								source="pageAndBlogEditor" // TODO: add isTemplateEditorCheck
																								collabService={CollabServiceProperty.NCS}
																								onProseMirrorRendered={onProseMirrorRendered}
																								isGuest={isExternalCollaborator}
																							>
																								<PrimaryToolbarContextProvider
																									value={{
																										forceDisabled,
																										userOffline,
																										collabLimitReached,
																										contentId,
																										contentType,
																										isContentCreationPanelOpen,
																										isUnpublishedDraft,
																										onClickPublish,
																										onClickOpenContentCreationPanel,
																										onPreviewClicked,
																										saveDraft,
																										spaceKey,
																										showRevertDialog,
																										retrieveEditorTitleAndContent,
																										isPublishingContent,
																										isSingleUserSession,
																										triggerAnalyticsEvent,
																										navigateOnClose: navigateOnCloseStable,
																										title,
																										editorActions,
																										disabled: disabled || collabLimitReached,
																										isTitleFocused,
																										collabEditState,
																										shouldRenderPreview,
																										shouldShowRecentSpotlight,
																										flags,
																										createAnalyticsEvent,
																										hasEditPermission,
																										ellipsisEditorActions: editorActionsProps,
																									}}
																								>
																									<ContentComponentsContextProvider
																										value={{
																											contentComponentsTitle: title ?? draftTitle,
																											contentComponentsDisabled:
																												disabled ||
																												collabLimitReached ||
																												!collabEditInitialized,
																											handleTitleChange,
																											breadcrumbsClickHandler:
																												handleBreadcrumbsClick,
																											beforeUpdateRequest,
																											postUpdateRequest,
																										}}
																									>
																										<MultiMacroQueryContainer
																											contentId={contentId}
																											featureFlags={featureFlags}
																											contentRendererMode={EDITOR}
																										>
																											<MentionsNotificationsWrapper
																												contentId={contentId}
																												flags={flags}
																												isLivePage={isLivePage}
																												isMentionsNotificationsEnabled={
																													isLivePage ||
																													isClassicMentionsNotificationsEnrolled
																												}
																												editorActions={editorActions}
																											>
																												<InlineCommentsProvider>
																													<ObjectCommentProvider>
																														<GuestMentionsNotificationsWrapper
																															dialogs={dialogs}
																															flags={flags}
																														>
																															<EditorLinkingFeatures
																																options={CARD_OPTIONS}
																																viewMode={
																																	editorExperiment(
																																		'live_pages_graceful_edit',
																																		'control',
																																	)
																																		? viewMode
																																		: isConsumption
																																}
																																isLivePage={isLivePage}
																															>
																																{(editorLinkingProps) => {
																																	const mainButtonsContext =
																																		usePrimaryToolbarContext();

																																	const primaryToolBarComponent =
																																		isEmbeddedConfluence ? (
																																			<EditorPrimaryToolbarComponents
																																				{...mainButtonsContext}
																																			/>
																																		) : undefined;

																																	return (
																																		<>
																																			{
																																				<QuickReload
																																					contentId={contentId}
																																					contentType={contentType}
																																					// The 'Date.now()' is expedient; see notes in quick-reload
																																					// package on what a more correct lastPollTime value would be.
																																					lastPollTime={Date.now()}
																																					pageMode={
																																						isLivePage
																																							? PageMode.LIVE
																																							: PageMode.EDITOR
																																					}
																																				/>
																																			}
																																			<ErrorBoundary
																																				attribution={
																																					Attribution.EDITOR_CORE
																																				}
																																			>
																																				<FullPageComposableEditor
																																					setEditorAPI={
																																						setEditorApi as any
																																					}
																																					// Needed to create the referentiality plugin in the
																																					// ComposableEditor
																																					referentialityContext={
																																						referentialityContext
																																					}
																																					editorPluginAIOptions={
																																						editorPluginAIOptions
																																					}
																																					aiGlobalOptIn={
																																						aiGlobalOptIn
																																					}
																																					editorPluginAIFeedbackHandlers={
																																						editorPluginAIFeedbackHandlers
																																					}
																																					// === Config ===
																																					{...extensionOptions}
																																					extensionProviders={
																																						extensionProvidersWithLCM
																																					}
																																					allowConfluenceInlineComment
																																					allowFindReplace={
																																						!collabEditInternetLost &&
																																						findReplaceOptions
																																					}
																																					defaultValue={
																																						draftBody ?? undefined
																																					}
																																					elementBrowser={
																																						elementBrowser
																																					}
																																					media={mediaOptions}
																																					mention={mentionProps}
																																					placeholder={
																																						placeholderText &&
																																						intl.formatMessage(
																																							placeholderText,
																																						)
																																					}
																																					waitForMediaUpload
																																					__rendererExtensionOptions={
																																						extensionRendererViewOptions
																																					}
																																					// === State config ===
																																					appearance={
																																						appearance ??
																																						'full-page'
																																					}
																																					disabled={
																																						isTitleFocused ||
																																						editorDisabled ||
																																						collabLimitReached
																																					}
																																					onChange={handleDocChange}
																																					shouldFocus={
																																						!!title &&
																																						shouldFocusEditor
																																					}
																																					onEditorReady={
																																						onEditorReady
																																					}
																																					// === Providers ===
																																					activityProvider={
																																						activityProvider
																																					}
																																					// In future, inlineCommentOptions should check if isTemplateEditor
																																					annotationProviders={
																																						annotationProviderOptions
																																					}
																																					autoformattingProvider={
																																						autoformattingProvider
																																					}
																																					collabEditProvider={
																																						collabEditProvider
																																					}
																																					collabEdit={collabEdit}
																																					contextIdentifierProvider={
																																						contextIdentifierProvider
																																					}
																																					emojiProvider={getEmojiProvider(
																																						EmojiResource,
																																						{
																																							cloudId,
																																							userId:
																																								userId || undefined,
																																							allowAnonymousAccess:
																																								true,
																																						},
																																					)}
																																					mentionProvider={
																																						mentionProvider
																																					}
																																					searchProvider={
																																						searchProvider
																																					}
																																					taskDecisionProvider={
																																						taskDecisionProvider
																																					}
																																					// === Components ===
																																					contentComponents={_getContentComponents(
																																						{
																																							contentId,
																																							debounceTitleChangeUpdates,
																																							focusEditor,
																																							onFocusChanged,
																																							userOffline,
																																							linkCreation:
																																								linkCreation ===
																																								'true',
																																							editorPluginAIFeedbackHandlers,
																																							isAdminHubAIEnabled,
																																							hideSpinner,
																																							templateSuggestionsRef,
																																							templateButtonRef,
																																							editorActions,
																																							isTitleFocused,
																																							disabled,
																																							viewMode,
																																							contentClassificationMetadata,
																																							appearance,
																																							spaceKey,
																																							title,
																																							cancelDebouncedSaveDraft,
																																							showSpinner,
																																							onClickOpenContentCreationPanel,
																																							isUnpublishedDraft,
																																							featureFlags,
																																							fullPageEditorPresetAPI,
																																							showBlankPageQuickActions,
																																							isLivePage,
																																							version,
																																							isEditorEmpty,
																																							shouldRenderPreview,
																																							isEditorAnnotationProviderManagersEnabled:
																																								fg(
																																									'confluence_frontend_editor_annotation_manager',
																																								),
																																							editorApi:
																																								fullPageEditorPresetAPI,
																																							contentType,
																																							onTitleChange,
																																							loadingPriority,
																																							isSpaceOverviewPage,
																																							isUnreadQueryLoading,
																																						},
																																					)}
																																					contextPanel={_getContextPanelComponent()}
																																					featureFlags={
																																						fullPageEditorFeatureFlags
																																					}
																																					performanceTracking={
																																						performanceTrackingOptions
																																					}
																																					{...editorLinkingProps}
																																					viewMode={
																																						viewMode
																																							? 'view'
																																							: 'edit'
																																					}
																																					__livePage={isLivePage}
																																					flags={flags}
																																					primaryToolbarComponents={
																																						primaryToolBarComponent
																																					}
																																					quickInsert={
																																						quickInsertOptions
																																					}
																																					hasEditPermission={
																																						hasEditPermission
																																					}
																																					requestToEditContent={
																																						requestToEditContent
																																					}
																																					hasRequestedEditPermission={
																																						hasRequestedEditPermission
																																					}
																																					getViewPagePath={
																																						getViewPagePath
																																					}
																																					contentId={contentId}
																																					spaceId={spaceId}
																																					isAIEnabledForContent={
																																						isAIEnabledForContent
																																					}
																																				/>
																																			</ErrorBoundary>

																																			<EditorCommentShortcutListener
																																				contentId={contentId}
																																				viewMode={viewMode}
																																			/>
																																		</>
																																	);
																																}}
																															</EditorLinkingFeatures>
																														</GuestMentionsNotificationsWrapper>
																													</ObjectCommentProvider>
																												</InlineCommentsProvider>
																											</MentionsNotificationsWrapper>
																										</MultiMacroQueryContainer>
																									</ContentComponentsContextProvider>
																								</PrimaryToolbarContextProvider>
																							</EditorAnalyticsListener>
																							<Spinner shouldShowSpinner={shouldShowSpinner} />
																						</EditorDisplayWrapper>
																						<AIOptInModal />

																						<EditorRevertDialog
																							contentId={contentId}
																							spaceKey={spaceKey}
																							isUnpublishedDraft={isUnpublishedDraft}
																							cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
																						/>
																						<OnboardingEditorModalPrompt
																							onTitleChange={onTitleChange}
																							handlePublish={() =>
																								onPublish({
																									suppressNotifyWatchers: false,
																								})()
																							}
																						/>
																						<ConditionalPublish
																							contentId={contentId}
																							spaceKey={spaceKey}
																							setCheckIfConditionPreventingPublish={
																								assignConditionalFunction
																							}
																						/>
																						<PageLoadWrapper
																							disabled={disabled}
																							contentId={contentId}
																							isCreate={isUnpublishedDraft}
																							proseMirrorRenderedData={proseMirrorRenderedData}
																							editSource={editSource}
																							extractedCollabEditProvider={
																								extractedCollabEditProvider
																							}
																							isLivePage={isLivePage}
																							customPerfData={customPerfData}
																							draftBody={draftBody}
																						/>
																						{shouldRenderPreview ? (
																							<RendererExtensionContext.Provider
																								value={rendererExtensionOptions}
																							>
																								<PreviewPageComponent
																									contentId={contentId}
																									spaceKey={spaceKey}
																									stagedContentId={stagedContentId}
																									stagedSpaceKey={stagedSpaceKey}
																									contentType={contentType as ContentTypeType}
																									contentAppearance={contentAppearance}
																									title={title}
																									editorActions={editorActions}
																									showTemplateVariablesInPreview
																									changeEditorMode={() =>
																										setShouldRenderPreview(false)
																									}
																									Renderer={PageContentRenderer}
																									isPageContentRenderer
																									editorType={EditorTypes.NCS}
																									previewAsBlog={previewAsBlog}
																									contentClassificationMetadata={
																										contentClassificationMetadata
																									}
																								/>
																							</RendererExtensionContext.Provider>
																						) : null}
																						{contentType === 'page' &&
																							isLivePagesFeatureEnabled && (
																								<ConvertToLivePageFlag />
																							)}
																					</EditorWrapper>
																				</HeadingLinksStyleWrapper>
																				{_getEditorContextConnectionsPanel({
																					referentialityContext,
																					connectionsPanelActiveNodeLocalId,
																					setConnectionsPanelActiveNodeLocalId,
																					closeConnectionsPanel,
																				})}
																				{isObjectSidebarShown &&
																					(fg('confluence_frontend_object_sidebar') ||
																						fg('ai-smart-button-team-25')) && (
																						<EditorObjectSidebarPanel />
																					)}
																				{
																					// This FF controls if the right rail config panel is available in when editing a page
																					isSmartButtonAllowed
																						? _getEditorContextSmartButtonConfigPanel({
																								smartButtonActiveNodeLocalId,
																								closeSmartButtonConfigPanel,
																								fullPageEditorPresetAPI,
																							})
																						: null
																				}
																				<EditorContextCustomSitesConfigPanelEntryPoint
																					{...{
																						localId: activeNodeLocalId || '',
																						closeSelf: closeConfigPanel,
																						editorApi: fullPageEditorPresetAPI || undefined,
																						contentId,
																						upsellModalSource,
																						setUpsellModalSource,
																					}}
																				/>
																			</BlankPageQuickActionsProvider>
																		</FullPageEditorComponentWrapper>
																	);
																}}
															</ContentAppearanceContextConsumer>
														</ContentAppearanceContextProvider>
													)}
												</EditorContextContentCreationPanelWrapper>
											</ClassificationModalContextProvider>
										</EditorContextPanelContextProvider>
									</SaveIndicatorContextProvider>
									<DocumentEditPageTitle
										contentType={contentType as ContentTypeType}
										isCreatingNew={isUnpublishedDraft}
										spaceKey={spaceKey}
										title={title}
										isLivePage={isLivePage}
									/>
									<EditorNavigationLockout
										contentId={contentId}
										isPublishingContent={isPublishingContent}
										isFirstParticipant={isFirstParticipant}
										editorPresetAPI={fullPageEditorPresetAPI}
									/>
								</EditorDatabasesOnboarding>
							</UFOLabel>
						);
					},
				), // withDialogs
			), // withFlags
		),
	),
);
